import { fromGid } from '@/lib/shopify/gid';
import { buildProductUrl } from '@/lib/elevar/cart-events';
import {
  getCompareAtPriceAmount,
  getElevarCurrencyCode,
  getMoneyAmount,
  getUserData,
  initElevarDataLayer,
  resolveElevarCategory,
} from '@/lib/elevar/shared';
import {
  ELEVAR_BRAND,
  ELEVAR_REFERRING_COLLECTION_KEY,
} from '@/lib/elevar/constants';

/**
 * Builds the `dl_view_item` product for the PDP's selected variant (falls back
 * to the first variant). Returns null until the Shopify product is available.
 * Also feeds Klaviyo's "Viewed Product" (lib/klaviyo/events.js).
 */
export function buildItemViewProduct(productData, selectedVariant, pricing) {
  const { shopifyProduct } = productData || {};
  const variant = selectedVariant || shopifyProduct?.variants?.[0];

  if (!variant || !shopifyProduct) return null;

  const storedReferrer =
    sessionStorage.getItem(ELEVAR_REFERRING_COLLECTION_KEY) || '';

  const { sale, full, hasPriceDifference } = pricing.variant(variant);

  return {
    id: variant?.sku || '',
    name: shopifyProduct?.title || '',
    brand: ELEVAR_BRAND,
    category: resolveElevarCategory(shopifyProduct),
    variant: variant?.title || '',
    price: getMoneyAmount(sale ?? variant?.price),
    compare_at_price: getCompareAtPriceAmount(hasPriceDifference, full, sale ?? variant?.price),
    list: storedReferrer,
    product_id: String(fromGid(shopifyProduct?.id || '')),
    variant_id: String(fromGid(variant?.id || '')),
    quantity: '1',
    url: buildProductUrl(shopifyProduct, variant),
    image: variant?.image?.url || shopifyProduct?.featuredImage?.url || '',
  };
}

/**
 * Pushes `dl_view_item` on PDP load.
 * `list` comes from `referringCollection` set by the collection page component.
 */
export default function elevarItemView(productData, selectedVariant, pricing) {
  if (!initElevarDataLayer()) return;

  const product = buildItemViewProduct(productData, selectedVariant, pricing);
  if (!product) return;

  const variant = selectedVariant || productData.shopifyProduct.variants[0];
  const storedReferrer = product.list;

  window.ElevarDataLayer.push({
    event: 'dl_view_item',
    user_properties: getUserData(),
    ecommerce: {
      currencyCode: getElevarCurrencyCode(variant),
      detail: {
        actionField: {
          list: storedReferrer,
          action: 'detail',
        },
        products: [product],
      },
    },
  });
}
